'use client'

import { useId } from 'react'
import { motion, useReducedMotion, type SVGMotionProps } from 'framer-motion'
import { cn } from '@/lib/utils'
import { GLOW_PATHS, MARK_PATHS, MARK_VIEWBOX } from './crucible-mark-paths'

/**
 * The Crucible logo mark: a crucible bowl, its rim, and a flame whose stem
 * pours through the centre. Vector data is exported from the Figma brand
 * file; see ./crucible-mark-paths.ts. The colours are the logo's own and are
 * intentionally not tied to the app's theme tokens.
 *
 * `CrucibleMarkAnimated` is the "Coremaster-Animated" loop from the same
 * file: bowls and rims stay still, the flame fill cycles gold to amber, and
 * three glows travel along the rims and down the stem over 4.2 seconds.
 */

const AMBER = '#D96B26'
const GOLD = '#FFD94D'
const GLOW = '#FFD973'

type Tone = 'brand' | 'current'

interface CrucibleMarkProps {
  className?: string
  /** `brand` = amber fill (default). `current` = monochrome via currentColor. */
  tone?: Tone
  /** Accessible name. Pass an empty string to mark the image decorative. */
  title?: string
  style?: React.CSSProperties
}

function a11yProps(title: string) {
  return title
    ? ({ role: 'img', 'aria-label': title } as const)
    : ({ 'aria-hidden': true } as const)
}

export function CrucibleMark({ className, tone = 'brand', title = 'Crucible', style }: CrucibleMarkProps) {
  return (
    <svg
      viewBox={MARK_VIEWBOX}
      style={style}
      className={cn('block h-8 w-auto shrink-0', className)}
      fill={tone === 'brand' ? AMBER : 'currentColor'}
      {...a11yProps(title)}
    >
      <path d={MARK_PATHS.bowlLeft} />
      <path d={MARK_PATHS.bowlRight} />
      <path d={MARK_PATHS.rimLeft} />
      <path d={MARK_PATHS.rimRight} />
      <path d={MARK_PATHS.flame} />
    </svg>
  )
}

const LOOP_SECONDS = 4.2
const GLOW_EASE: [number, number, number, number] = [0.15, 0.7, 0.35, 1]
const GLOW_STROKE = {
  fill: 'none',
  stroke: GLOW,
  strokeOpacity: 0.9,
  strokeWidth: 200,
  strokeLinecap: 'round',
  pathLength: 1,
} as const

/**
 * Animated loop of the mark. Falls back to the static mark when the viewer
 * prefers reduced motion.
 */
export function CrucibleMarkAnimated({ className, title = 'Crucible' }: Omit<CrucibleMarkProps, 'tone'>) {
  const id = useId()
  const reduceMotion = useReducedMotion()
  if (reduceMotion) return <CrucibleMark className={className} title={title} />

  const clipRimLeft = `${id}-rim-l`
  const clipRimRight = `${id}-rim-r`
  const clipStem = `${id}-stem`
  const blur = `${id}-blur`

  // Keyframe fractions of the 4.2s loop, from the Figma motion export.
  const rimGlow = {
    initial: { opacity: 0, strokeDasharray: '0.1 1', strokeDashoffset: 0 },
    animate: { opacity: [0, 1, 1, 0, 0], strokeDashoffset: [0, -0.9, -0.9] },
    transition: {
      opacity: { duration: LOOP_SECONDS, times: [0, 0.0238, 0.3333, 0.381, 1], ease: ['easeOut', 'linear', 'easeIn', 'linear'], repeat: Infinity },
      strokeDashoffset: { duration: LOOP_SECONDS, times: [0, 0.381, 1], ease: [GLOW_EASE, 'linear'], repeat: Infinity },
    },
  } satisfies Pick<SVGMotionProps<SVGPathElement>, 'initial' | 'animate' | 'transition'>

  return (
    <svg viewBox={MARK_VIEWBOX} className={cn('block h-8 w-auto shrink-0', className)} {...a11yProps(title)}>
      <defs>
        <clipPath id={clipRimLeft}><path d={MARK_PATHS.rimLeft} /></clipPath>
        <clipPath id={clipRimRight}><path d={MARK_PATHS.rimRight} /></clipPath>
        <clipPath id={clipStem}><path d={MARK_PATHS.flame} /></clipPath>
        <filter id={blur} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      {/* Static structure: bowls and rims never move. */}
      <g fill={AMBER}>
        <path d={MARK_PATHS.bowlLeft} />
        <path d={MARK_PATHS.bowlRight} />
        <path d={MARK_PATHS.rimLeft} />
        <path d={MARK_PATHS.rimRight} />
      </g>

      {/* Flame: gold ignition, settles to amber, breathes back to gold. */}
      <motion.path
        d={MARK_PATHS.flame}
        initial={{ fill: GOLD }}
        animate={{ fill: [GOLD, AMBER, AMBER, GOLD, GOLD] }}
        transition={{
          fill: {
            duration: LOOP_SECONDS,
            times: [0, 0.2143, 0.4762, 0.8571, 1],
            ease: [[0.25, 0.1, 0.25, 1], 'linear', [0.25, 0.1, 0.25, 1], 'linear'],
            repeat: Infinity,
          },
        }}
      />

      {/* Rim glows fire together, left and right, clipped to the rim shapes. */}
      <g clipPath={`url(#${clipRimLeft})`} filter={`url(#${blur})`}>
        <motion.path
          d={GLOW_PATHS.rimLeft.d}
          transform={`translate(${GLOW_PATHS.rimLeft.tx} ${GLOW_PATHS.rimLeft.ty})`}
          {...GLOW_STROKE}
          {...rimGlow}
        />
      </g>
      <g clipPath={`url(#${clipRimRight})`} filter={`url(#${blur})`}>
        <motion.path
          d={GLOW_PATHS.rimRight.d}
          transform={`translate(${GLOW_PATHS.rimRight.tx} ${GLOW_PATHS.rimRight.ty})`}
          {...GLOW_STROKE}
          {...rimGlow}
        />
      </g>

      {/* Stem glow: starts 0.4s after the rims and pours down the stem. */}
      <g clipPath={`url(#${clipStem})`} filter={`url(#${blur})`}>
        <motion.path
          d={GLOW_PATHS.stem.d}
          transform={`translate(${GLOW_PATHS.stem.tx} ${GLOW_PATHS.stem.ty})`}
          {...GLOW_STROKE}
          initial={{ opacity: 0, strokeDasharray: '0.15 1', strokeDashoffset: 0 }}
          animate={{
            opacity: [0, 0, 1, 1, 0, 0],
            strokeDasharray: ['0.15 1', '0.15 1', '0.3571 1', '0.3571 1'],
            strokeDashoffset: [0, 0, -0.6429, -0.6429],
          }}
          transition={{
            opacity: { duration: LOOP_SECONDS, times: [0, 0.0952, 0.131, 0.3333, 0.381, 1], ease: ['linear', 'easeOut', 'linear', 'easeIn', 'linear'], repeat: Infinity },
            strokeDasharray: { duration: LOOP_SECONDS, times: [0, 0.095, 0.381, 1], ease: [[0.5, 0, 0.5, 1], GLOW_EASE, 'linear'], repeat: Infinity },
            strokeDashoffset: { duration: LOOP_SECONDS, times: [0, 0.095, 0.381, 1], ease: [[0.5, 0, 0.5, 1], GLOW_EASE, 'linear'], repeat: Infinity },
          }}
        />
      </g>
    </svg>
  )
}
